// Explorer metadata: FK relationships, container list, and types.
// Separated from ExplorerTab.tsx so schema changes don't touch UI code.

export interface FkTarget {
  container: string;
  label: string;
  /** Fields to show in hover tooltip when this FK is detected. Uses dot notation for nested fields. */
  displayFields?: string[];
}

/** Default display fields per container — used when FkTarget doesn't specify its own */
export const CONTAINER_DISPLAY_FIELDS: Record<string, string[]> = {
  // Core learning
  users: ['name', 'email', 'ring'],
  questions: ['questionText', 'subject', 'difficulty', 'importStatus'],
  'question-revisions': ['questionId', 'editedBy'],
  modules: ['displayName', 'subject', 'parentModuleId'],
  subjects: ['name', 'displayName'],
  submissions: ['userId', 'questionId', 'submittedAt'],
  'student-solutions': ['userId', 'questionId', 'inputMode', 'status', 'elapsedSeconds'],
  'hint-requests': ['userId', 'questionId'],
  'concept-lab': ['userId', 'submissionId', 'status'],
  'diagnosis-results': ['userId', 'subject', 'status'],
  // Scores / progress / goals
  user_module_scores: ['userId', 'moduleId', 'subject'],
  user_question_scores: ['userId', 'questionId', 'moduleId'],
  'daily-progress': ['userId', 'date'],
  'user-daily-activity': ['userId', 'date'],
  goals: ['userId', 'title', 'status'],
  plans: ['userId', 'status'],
  'user-paper-unlocks': ['userId'],
  'user-error-log': ['userId', 'errorType'],
  // Curriculum / concepts
  curricula: ['name', 'subject', 'grade'],
  'curriculum-placements': ['curriculumId', 'moduleId'],
  'curriculum-instances': ['ownerId', 'curriculumId'],
  concepts: ['name', 'subject'],
  'concept-simulations': ['name', 'subject', 'status'],
  // Chats / sessions
  'text-chats': ['userId', 'questionId'],
  'voice-chats': ['userId', 'questionId'],
  'study-sessions': ['userId', 'subject'],
  videos: ['userId', 'submissionId'],
  'photo-sessions': ['userId', 'status'],
  // Question sets / tests
  'question-sets': ['title', 'subject', 'questionCount'],
  'test-sessions': ['status', 'questionSetTitle', 'totalQuestions'],
  'test-assignments': ['studentId', 'questionSetId', 'status'],
  'assignment-acts': ['questionSetTitle', 'assignedByName', 'status'],
  // Social / duels
  duels: ['status', 'subject'],
  'duel-ratings': ['userId', 'rating'],
  friendships: ['userId', 'friendId', 'status'],
  handles: ['handle', 'userId'],
  // Teachers / batches / tenancy
  teachers: ['displayName', 'userId'],
  'progress-reports': ['teacherId', 'studentId'],
  batches: ['name', 'ownerId', 'subject'],
  'batch-memberships': ['batchId', 'studentId', 'status'],
  'student-batches': ['name', 'teacherId'],
  tenants: ['name', 'status'],
  // Notifications
  'notification-events': ['userId', 'type', 'status'],
  'notification-rules': ['name', 'type'],
  'notification-intents': ['userId', 'type', 'status'],
  'notification-deliveries': ['userId', 'channel', 'status'],
  'notification-preferences': ['userId'],
  'whatsapp-messages': ['userId', 'status'],
  // Learning moments / what's new
  'learning-moments': ['scope', 'subject'],
  'student-learning-moments': ['userId', 'learningMomentId'],
  'whats-new-tips': ['title', 'appTarget'],
  'whats-new-tips-views': ['userId', 'tipId'],
  // Paper import
  'paperimport-imports': ['name', 'status', 'subject'],
  'import-log': ['status', 'type'],
  // Model I/O + evals
  modelInputs: ['submissionId', 'userId'],
  modelOutputs: ['inputId', 'parameters.provider'],
  'eval-datasets': ['name', 'category'],
  'eval-dataset-items': ['datasetId'],
  'eval-runs': ['name', 'status'],
  'eval-comparisons': ['name'],
  'eval-scenario-inputs': ['datasetId', 'name'],
  'eval-scenario-runs': ['name', 'status'],
  'eval-scenario-run-items': ['datasetId', 'status'],
  'eval-judge-runs': ['name', 'status'],
  // Agents
  'agent-runs': ['personaId', 'status'],
  'agent-sessions': ['runId', 'status'],
  'agent-personas': ['name', 'status'],
  'agent-feedback': ['personaId', 'runId'],
  'agent-journal': ['personaId', 'runId'],
  // Config / ops
  prompts: ['name', 'type', 'version'],
  'pipeline-config-revisions': ['name'],
  'user-tags': ['userId', 'tag'],
  threads: ['type', 'status', 'ownerId', 'title', 'objectType', 'objectId', 'createdBy'],
  'lifecycle-events': ['userId', 'event'],
  'async-jobs': ['userId', 'type', 'status'],
  'audit-logs': ['category', 'action'],
  'otp-codes': ['email', 'purpose'],
  'rate-limits': ['userId', 'key'],
  'api-metrics': ['route'],
  'llm-metrics': ['provider'],
  'page-perf': ['route'],
  tracingLogs: ['level'],
};

// Scalar FK fields: container → { fieldPath → target }
// Dot-notation paths handle nested FKs (e.g. "parameters.promptId")
// Source of truth: Backend/functions/DBSchema.md
export const FK_MAP: Record<string, Record<string, FkTarget>> = {
  questions: {
    moduleId: { container: 'modules', label: 'Module' },
    sourceImportId: { container: 'paperimport-imports', label: 'Paper Import' },
  },
  submissions: {
    userId: { container: 'users', label: 'User' },
    questionId: { container: 'questions', label: 'Question' },
    modelInputId: { container: 'modelInputs', label: 'Model Input' },
    rerunFromSubmissionId: { container: 'submissions', label: 'Rerun From' },
    videoRecordingId: { container: 'videos', label: 'Video' },
  },
  user_module_scores: {
    userId: { container: 'users', label: 'User' },
    moduleId: { container: 'modules', label: 'Module' },
  },
  prompts: {
    basePromptId: { container: 'prompts', label: 'Base Prompt' },
  },
  'hint-requests': {
    userId: { container: 'users', label: 'User' },
    questionId: { container: 'questions', label: 'Question' },
    modelInputId: { container: 'modelInputs', label: 'Model Input' },
    modelOutputId: { container: 'modelOutputs', label: 'Model Output' },
  },
  modules: {
    parentModuleId: { container: 'modules', label: 'Parent Module' },
  },
  user_question_scores: {
    userId: { container: 'users', label: 'User' },
    questionId: { container: 'questions', label: 'Question' },
    moduleId: { container: 'modules', label: 'Module' },
  },
  modelInputs: {
    userId: { container: 'users', label: 'User' },
    submissionId: { container: 'submissions', label: 'Submission' },
    datasetId: { container: 'eval-datasets', label: 'Dataset' },
  },
  modelOutputs: {
    inputId: { container: 'modelInputs', label: 'Model Input' },
    userId: { container: 'users', label: 'User' },
    submissionId: { container: 'submissions', label: 'Submission' },
    evalRunId: { container: 'eval-runs', label: 'Eval Run' },
    datasetItemId: { container: 'eval-dataset-items', label: 'Dataset Item' },
    datasetId: { container: 'eval-datasets', label: 'Dataset' },
    'parameters.promptId': { container: 'prompts', label: 'Prompt' },
  },
  'eval-dataset-items': {
    datasetId: { container: 'eval-datasets', label: 'Dataset' },
    modelInputId: { container: 'modelInputs', label: 'Model Input' },
    'labelData.labeledOutputId': { container: 'modelOutputs', label: 'Labeled Output' },
  },
  'eval-runs': {
    datasetId: { container: 'eval-datasets', label: 'Dataset' },
    'parameters.promptId': { container: 'prompts', label: 'Prompt' },
  },
  'eval-comparisons': {
    baselineRunId: { container: 'eval-runs', label: 'Baseline Run' },
    candidateRunId: { container: 'eval-runs', label: 'Candidate Run' },
  },
  teachers: {
    userId: { container: 'users', label: 'User' },
  },
  student_teacher_map: {
    studentId: { container: 'users', label: 'Student' },
    teacherId: { container: 'users', label: 'Teacher' },
  },
  'daily-progress': {
    userId: { container: 'users', label: 'User' },
  },
  videos: {
    userId: { container: 'users', label: 'User' },
    submissionId: { container: 'submissions', label: 'Submission' },
  },
  'text-chats': {
    userId: { container: 'users', label: 'User' },
    questionId: { container: 'questions', label: 'Question' },
    submissionId: { container: 'submissions', label: 'Submission' },
    modelInputId: { container: 'modelInputs', label: 'Model Input' },
  },
  'voice-chats': {
    userId: { container: 'users', label: 'User' },
    questionId: { container: 'questions', label: 'Question' },
    submissionId: { container: 'submissions', label: 'Submission' },
    modelInputId: { container: 'modelInputs', label: 'Model Input' },
  },
  'study-sessions': {
    userId: { container: 'users', label: 'User' },
  },
  'question-sets': {
    createdBy: { container: 'users', label: 'Creator' },
  },
  'test-sessions': {
    userId: { container: 'users', label: 'User' },
    questionSetId: { container: 'question-sets', label: 'Question Set' },
    'questionResults.questionId': { container: 'questions', label: 'Question' },
    'questionResults.submissionId': { container: 'submissions', label: 'Submission' },
  },
  'test-assignments': {
    studentId: { container: 'users', label: 'Student' },
    assignedById: { container: 'users', label: 'Assigned By' },
    questionSetId: { container: 'question-sets', label: 'Question Set' },
  },
  'assignment-acts': {
    assignedById: { container: 'users', label: 'Assigned By' },
    questionSetId: { container: 'question-sets', label: 'Question Set' },
    questionId: { container: 'questions', label: 'Question' },
    batchId: { container: 'batches', label: 'Batch' },
    studentId: { container: 'users', label: 'Student' },
  },
  'progress-reports': {
    teacherId: { container: 'users', label: 'Teacher' },
    studentId: { container: 'users', label: 'Student' },
  },
  'teacher-questions': {
    teacherId: { container: 'users', label: 'Teacher' },
    moduleId: { container: 'modules', label: 'Module' },
  },
  'user-tags': {
    userId: { container: 'users', label: 'User' },
  },
  'student-batches': {
    teacherId: { container: 'users', label: 'Teacher' },
  },
  'learning-moments': {},
  'student-learning-moments': {
    userId: { container: 'users', label: 'User' },
    learningMomentId: { container: 'learning-moments', label: 'Learning Moment' },
  },
  'student-solutions': {
    userId: { container: 'users', label: 'User' },
    questionId: { container: 'questions', label: 'Question' },
    testSessionId: { container: 'test-sessions', label: 'Test Session' },
    lastSubmissionId: { container: 'submissions', label: 'Last Submission' },
  },
  threads: {
    ownerId: { container: 'users', label: 'Owner (Teacher)' },
    assignedTo: { container: 'users', label: 'Assigned To' },
    createdBy: { container: 'users', label: 'Created By (Student)' },
  },
  // ── Newer containers ──────────────────────────────────────────────────────
  'concept-lab': {
    userId: { container: 'users', label: 'User' },
    submissionId: { container: 'submissions', label: 'Submission' },
    questionId: { container: 'questions', label: 'Question' },
  },
  'diagnosis-results': {
    userId: { container: 'users', label: 'User' },
  },
  'user-daily-activity': {
    userId: { container: 'users', label: 'User' },
  },
  goals: {
    userId: { container: 'users', label: 'User' },
  },
  plans: {
    userId: { container: 'users', label: 'User' },
  },
  'user-paper-unlocks': {
    userId: { container: 'users', label: 'User' },
  },
  'user-error-log': {
    userId: { container: 'users', label: 'User' },
  },
  'curriculum-placements': {
    curriculumId: { container: 'curricula', label: 'Curriculum' },
    moduleId: { container: 'modules', label: 'Module' },
  },
  'curriculum-instances': {
    ownerId: { container: 'users', label: 'Owner' },
    curriculumId: { container: 'curricula', label: 'Curriculum' },
  },
  'question-revisions': {
    questionId: { container: 'questions', label: 'Question' },
  },
  duels: {
    challengerId: { container: 'users', label: 'Challenger' },
    opponentId: { container: 'users', label: 'Opponent' },
    questionSetId: { container: 'question-sets', label: 'Question Set' },
  },
  'duel-ratings': {
    userId: { container: 'users', label: 'User' },
  },
  friendships: {
    userId: { container: 'users', label: 'User' },
    friendId: { container: 'users', label: 'Friend' },
  },
  handles: {
    userId: { container: 'users', label: 'User' },
  },
  batches: {
    ownerId: { container: 'users', label: 'Owner (Teacher)' },
  },
  'batch-memberships': {
    batchId: { container: 'batches', label: 'Batch' },
    studentId: { container: 'users', label: 'Student' },
  },
  'notification-events': {
    userId: { container: 'users', label: 'User' },
  },
  'notification-intents': {
    userId: { container: 'users', label: 'User' },
    ruleId: { container: 'notification-rules', label: 'Rule' },
  },
  'notification-deliveries': {
    userId: { container: 'users', label: 'User' },
    intentId: { container: 'notification-intents', label: 'Intent' },
  },
  'notification-preferences': {
    userId: { container: 'users', label: 'User' },
  },
  'whatsapp-messages': {
    userId: { container: 'users', label: 'User' },
  },
  'lifecycle-events': {
    userId: { container: 'users', label: 'User' },
  },
  'async-jobs': {
    userId: { container: 'users', label: 'User' },
  },
  'photo-sessions': {
    userId: { container: 'users', label: 'User' },
  },
  'whats-new-tips-views': {
    userId: { container: 'users', label: 'User' },
  },
  'eval-judge-runs': {
    datasetId: { container: 'eval-datasets', label: 'Dataset' },
    runId: { container: 'eval-runs', label: 'Eval Run' },
  },
  'eval-scenario-inputs': {
    datasetId: { container: 'eval-datasets', label: 'Dataset' },
  },
  'eval-scenario-runs': {
    datasetId: { container: 'eval-datasets', label: 'Dataset' },
  },
  'eval-scenario-run-items': {
    datasetId: { container: 'eval-datasets', label: 'Dataset' },
    runId: { container: 'eval-scenario-runs', label: 'Scenario Run' },
  },
  'agent-runs': {
    personaId: { container: 'agent-personas', label: 'Persona' },
  },
  'agent-sessions': {
    runId: { container: 'agent-runs', label: 'Agent Run' },
    personaId: { container: 'agent-personas', label: 'Persona' },
  },
  'agent-feedback': {
    personaId: { container: 'agent-personas', label: 'Persona' },
    runId: { container: 'agent-runs', label: 'Agent Run' },
  },
  'agent-journal': {
    personaId: { container: 'agent-personas', label: 'Persona' },
    runId: { container: 'agent-runs', label: 'Agent Run' },
  },
};

// Array FK fields: container → { fieldName → target }
// Each element of the array is treated as an FK ID
// Source of truth: Backend/functions/DBSchema.md
export const ARRAY_FK_MAP: Record<string, Record<string, FkTarget>> = {
  'question-sets': {
    questionIds: { container: 'questions', label: 'Question' },
  },
  'paperimport-imports': {
    extractedQuestionIds: { container: 'questions', label: 'Question' },
  },
  'study-sessions': {
    questionIds: { container: 'questions', label: 'Question' },
  },
  'test-sessions': {
    questionIds: { container: 'questions', label: 'Question' },
  },
  'assignment-acts': {
    studentIds: { container: 'users', label: 'Student' },
  },
};

// All known CosmosDB containers (sorted). Mirrors the authoritative `Containers`
// enum in Backend/functions/src/shared/cosmosClient.ts — keep in sync when a new
// container is added there. (`tracingLogs` + legacy read-only `student-batches`
// are managed outside that enum; `teacher-questions` is retired → merged into
// `questions`.)
export const CONTAINERS = [
  // Core learning
  'users', 'questions', 'question-revisions', 'modules', 'subjects', 'submissions',
  'student-solutions', 'hint-requests', 'concept-lab', 'diagnosis-results',
  // Scores / progress / goals
  'user_module_scores', 'user_question_scores', 'daily-progress', 'user-daily-activity',
  'goals', 'plans', 'user-paper-unlocks', 'user-error-log',
  // Curriculum / concepts
  'curricula', 'curriculum-placements', 'curriculum-instances', 'concepts', 'concept-simulations',
  // Chats / sessions
  'text-chats', 'voice-chats', 'study-sessions', 'videos', 'photo-sessions',
  // Question sets / tests / assignments
  'question-sets', 'test-sessions', 'test-assignments', 'assignment-acts',
  // Social / duels
  'duels', 'duel-ratings', 'friendships', 'handles',
  // Teachers / batches / tenancy
  'teachers', 'student_teacher_map', 'progress-reports', 'batches', 'batch-memberships',
  'student-batches', 'tenants',
  // Notifications
  'notification-events', 'notification-rules', 'notification-intents',
  'notification-deliveries', 'notification-preferences', 'whatsapp-messages',
  // Learning moments / what's new
  'learning-moments', 'student-learning-moments', 'whats-new-tips', 'whats-new-tips-views',
  // Paper import
  'paperimport-imports', 'import-log',
  // Model I/O + evals
  'modelInputs', 'modelOutputs', 'eval-datasets', 'eval-dataset-items', 'eval-runs',
  'eval-comparisons', 'eval-scenario-inputs', 'eval-scenario-runs', 'eval-scenario-run-items',
  'eval-judge-runs',
  // Agents
  'agent-runs', 'agent-sessions', 'agent-personas', 'agent-feedback', 'agent-journal',
  // Config / ops / infra
  'prompts', 'pipeline-config-revisions', 'settings', 'user-tags', 'threads',
  'lifecycle-events', 'async-jobs', 'audit-logs', 'otp-codes', 'rate-limits',
  'api-metrics', 'llm-metrics', 'page-perf', 'tracingLogs',
].sort();

export interface NavigationEntry {
  container: string;
  id: string;
  label: string;
}

// ─── Reverse FK Map ─────────────────────────────────────────────────────────
// "Which documents in other containers point to a document in THIS container?"
// Derived automatically from FK_MAP and ARRAY_FK_MAP.
export interface ReverseFkEntry {
  sourceContainer: string; // container that holds the FK field
  sourceField: string;     // field name in that container
  label: string;           // display label (e.g. "modelOutputs via modelInputId")
  isArray: boolean;        // true = ARRAY_CONTAINS query, false = scalar equality
}

function buildReverseFkMap(): Record<string, ReverseFkEntry[]> {
  const result: Record<string, ReverseFkEntry[]> = {};

  for (const [sourceContainer, fields] of Object.entries(FK_MAP)) {
    for (const [fieldPath, target] of Object.entries(fields)) {
      if (!result[target.container]) result[target.container] = [];
      result[target.container].push({
        sourceContainer,
        sourceField: fieldPath,
        label: `${sourceContainer} via ${fieldPath}`,
        isArray: false,
      });
    }
  }

  for (const [sourceContainer, fields] of Object.entries(ARRAY_FK_MAP)) {
    for (const [fieldName, target] of Object.entries(fields)) {
      if (!result[target.container]) result[target.container] = [];
      result[target.container].push({
        sourceContainer,
        sourceField: fieldName,
        label: `${sourceContainer} via ${fieldName}`,
        isArray: true,
      });
    }
  }

  // Sort each list by sourceContainer for stable display order
  for (const entries of Object.values(result)) {
    entries.sort((a, b) => a.sourceContainer.localeCompare(b.sourceContainer));
  }

  return result;
}

export const REVERSE_FK_MAP = buildReverseFkMap();
